<template>
  <div>
    <v-breadcrumbs :items="breadcrumbItems" />

    <v-container fluid>
      <v-row>
        <v-col cols="12" lg="5">
          <v-card>
            <v-card-title>Push Notifications</v-card-title>
            <v-card-text>
              <v-form ref="form" v-model="isValid" @submit.prevent="send">
                <v-btn-toggle
                  v-model="mode"
                  mandatory
                  dense
                  class="mb-5"
                  color="primary"
                >
                  <v-btn value="broadcast">
                    <v-icon left>mdi-bullhorn-outline</v-icon>
                    All
                  </v-btn>
                  <v-btn value="online">
                    <v-icon left>mdi-access-point</v-icon>
                    Online
                  </v-btn>
                  <v-btn value="email">
                    <v-icon left>mdi-email-outline</v-icon>
                    Email
                  </v-btn>
                  <v-btn value="token">
                    <v-icon left>mdi-cellphone-message</v-icon>
                    Token
                  </v-btn>
                </v-btn-toggle>

                <v-text-field
                  v-model.trim="apiBaseUrl"
                  label="API base URL"
                  outlined
                  dense
                  :rules="[required]"
                />

                <v-text-field
                  v-model.trim="secret"
                  label="Admin secret"
                  type="password"
                  outlined
                  dense
                  :rules="[required]"
                />

                <v-text-field
                  v-if="mode === 'email'"
                  v-model.trim="email"
                  label="User email"
                  outlined
                  dense
                  :rules="[required]"
                />

                <v-textarea
                  v-if="mode === 'token'"
                  v-model.trim="fcmToken"
                  label="FCM token"
                  outlined
                  dense
                  rows="3"
                  :rules="[required]"
                />

                <v-text-field
                  v-model.trim="title"
                  label="Title"
                  outlined
                  dense
                  :rules="[required]"
                />

                <v-textarea
                  v-model.trim="body"
                  label="Body"
                  outlined
                  dense
                  rows="4"
                  :rules="[required]"
                />

                <v-select
                  v-model="screen"
                  :items="screenOptions"
                  item-text="label"
                  item-value="value"
                  label="Screen"
                  outlined
                  dense
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.label }}</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ item.description }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ item.label }}
                  </template>
                </v-select>

                <v-text-field
                  v-model.trim="relatedId"
                  label="Related ID"
                  outlined
                  dense
                  :hint="selectedScreenDescription"
                  persistent-hint
                />

                <v-alert v-if="error" type="error" dense text class="mb-4">
                  {{ error }}
                </v-alert>

                <v-btn
                  color="primary"
                  type="submit"
                  :loading="isSending"
                  :disabled="!isValid || isSending"
                >
                  <v-icon left>mdi-send</v-icon>
                  Send
                </v-btn>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" lg="7">
          <v-row>
            <v-col cols="12" sm="4">
              <v-card>
                <v-card-title class="text-subtitle-1"
                  >Connected Clients</v-card-title
                >
                <v-card-text class="text-h4">{{ clients.length }}</v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" sm="4">
              <v-card>
                <v-card-title class="text-subtitle-1"
                  >Current Target</v-card-title
                >
                <v-card-text class="text-h6">{{ targetLabel }}</v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" sm="4">
              <v-card>
                <v-card-title class="text-subtitle-1">Last Result</v-card-title>
                <v-card-text class="text-h6">
                  {{ lastResultStatus }}
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-card class="mt-4">
            <v-card-title>Response</v-card-title>
            <v-card-text>
              <pre class="response-output">{{ formattedResponse }}</pre>
            </v-card-text>
          </v-card>

          <v-card class="mt-4">
            <v-card-title>
              Connected Users
              <v-spacer />
              <v-btn
                small
                outlined
                :loading="isLoadingOnlineUsers"
                @click="loadOnlineUsers"
              >
                <v-icon left>mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-alert
                v-if="onlineUsersError"
                type="error"
                dense
                text
                class="mb-4"
              >
                {{ onlineUsersError }}
              </v-alert>

              <v-data-table
                :headers="onlineUserHeaders"
                :items="onlineUsers"
                :loading="isLoadingOnlineUsers"
                :footer-props="footerProps"
                item-key="userId"
                dense
              >
                <template v-slot:item.email="{ item }">
                  {{ item.email || "Unknown" }}
                </template>

                <template v-slot:item.name="{ item }">
                  {{ item.name || "-" }}
                </template>

                <template v-slot:item.socketIds="{ item }">
                  <div class="socket-list">
                    <code v-for="socketId in item.socketIds" :key="socketId">
                      {{ socketId }}
                    </code>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "PushNotifications",

  data() {
    return {
      mode: "broadcast",
      apiBaseUrl: "",
      secret: "",
      email: "",
      fcmToken: "",
      title: "",
      body: "",
      screen: "Dashboard",
      screenOptions: [
        {
          label: "Dashboard",
          value: "Dashboard",
          description: "Home tab, linking path: app/home",
        },
        {
          label: "Subscriptions",
          value: "Subs",
          description: "Subscriptions tab, linking path: app/subscriptions",
        },
        {
          label: "Budget",
          value: "Budget",
          description: "Budget tab, linking path: app/budget",
        },
        {
          label: "Transactions",
          value: "Transactions",
          description: "Transactions tab, linking path: app/transactions",
        },
        {
          label: "Settings",
          value: "Settings",
          description: "Settings tab, linking path: app/settings",
        },
        {
          label: "Messages",
          value: "Messages",
          description: "Notification inbox, linking path: app/messages",
        },
        {
          label: "Track My Letter",
          value: "TrackMyLetter",
          description: "Letter tracking, linking path: app/track-letter/:id",
        },
        {
          label: "Cancellation History",
          value: "CancellationHistory",
          description:
            "Cancellation history, linking path: app/cancellation-history",
        },
        {
          label: "News & Updates",
          value: "NewsUpdates",
          description: "News screen, linking path: app/news",
        },
        {
          label: "Support",
          value: "Support",
          description: "Support screen, linking path: app/support",
        },
        {
          label: "Tutorial",
          value: "Tutorial",
          description: "Tutorial screen, linking path: app/tutorial",
        },
        {
          label: "Premium History",
          value: "MyUpgradedSubscriptions",
          description:
            "Premium purchase history, linking path: app/premium-history",
        },
        {
          label: "Upgrade Subscription",
          value: "UpgradeSubscription",
          description: "Upgrade screen, linking path: app/upgrade",
        },
        {
          label: "Subscription Detail",
          value: "SubscriptionDetail",
          description:
            "Subscription detail, linking path: app/subscriptions/:id",
        },
        {
          label: "Multiple Subscription Details",
          value: "MultipleSubscriptionsDetail",
          description:
            "Hidden dashboard route for grouped subscription details",
        },
        {
          label: "Cancellation Letter",
          value: "CancellationLetter",
          description: "Hidden dashboard route for cancellation letter flow",
        },
        {
          label: "Cancellation Flow",
          value: "CancellationFlow",
          description: "Hidden settings cancellation flow route",
        },
        {
          label: "Stripe Payment Callback",
          value: "StripeCheck",
          description: "Payment callback, linking path: app/payment-callback",
        },
        {
          label: "Mollie Payment Callback",
          value: "MollieCheck",
          description: "Payment callback, linking path: app/mollie-callback",
        },
      ],
      relatedId: "",
      isValid: false,
      isSending: false,
      isLoadingOnlineUsers: false,
      error: "",
      onlineUsersError: "",
      response: null,
      onlineUsers: [],
      footerProps: {
        "items-per-page-options": [10, 25, 100, -1],
      },
      onlineUserHeaders: [
        {
          text: "User ID",
          value: "userId",
        },
        {
          text: "Email",
          value: "email",
        },
        {
          text: "Name",
          value: "name",
        },
        {
          text: "Sockets",
          value: "socketCount",
        },
        {
          text: "Socket IDs",
          value: "socketIds",
          sortable: false,
        },
        {
          text: "Connected At",
          value: "connectedAt",
        },
      ],
      required: (value) => Boolean(value) || "Required",
    };
  },

  computed: {
    ...mapState({
      clients: (state) => state.main.clients,
      serverUrl: (state) => state.connection.serverUrl,
    }),
    breadcrumbItems() {
      return [
        {
          text: "Push Notifications",
          disabled: true,
        },
      ];
    },
    endpoint() {
      switch (this.mode) {
        case "online":
          return "/auth/push-notifications/broadcast-online";
        case "email":
          return "/auth/push-notifications/send-to-email";
        case "token":
          return "/auth/push-notifications/test-send";
        case "broadcast":
        default:
          return "/auth/push-notifications/broadcast";
      }
    },
    formattedResponse() {
      return this.response
        ? JSON.stringify(this.response, null, 2)
        : "No notification sent yet.";
    },
    lastResultStatus() {
      if (!this.response) {
        return "Waiting";
      }

      return this.response.success ? "Success" : "Failed";
    },
    targetLabel() {
      switch (this.mode) {
        case "online":
          return "Online users";
        case "email":
          return this.email || "One email";
        case "token":
          return "One device";
        case "broadcast":
        default:
          return "All users";
      }
    },
    selectedScreenDescription() {
      const selected = this.screenOptions.find(
        (option) => option.value === this.screen
      );
      return selected ? selected.description : "";
    },
  },

  mounted() {
    this.apiBaseUrl =
      localStorage.getItem("push_api_base_url") || window.location.origin;
    this.secret = this.cleanValue(localStorage.getItem("push_admin_secret"));
    if (this.secret) {
      this.loadOnlineUsers();
    }
  },

  methods: {
    cleanValue(value) {
      return String(value || "").trim();
    },
    persistSettings() {
      this.secret = this.cleanValue(this.secret);
      this.apiBaseUrl = this.cleanValue(this.apiBaseUrl);
      localStorage.setItem("push_admin_secret", this.secret);
      localStorage.setItem("push_api_base_url", this.apiBaseUrl);
    },
    buildPayload() {
      const payload = {
        title: this.title,
        body: this.body,
        screen: this.screen || "Dashboard",
        secret: this.cleanValue(this.secret),
      };

      if (this.mode === "email") {
        payload.email = this.email;
      }

      if (this.mode === "token") {
        payload.fcmToken = this.fcmToken;
      }

      payload.relatedId = this.relatedId || null;

      return payload;
    },
    async loadOnlineUsers() {
      this.onlineUsersError = "";

      this.isLoadingOnlineUsers = true;
      this.persistSettings();

      try {
        const baseUrl = this.apiBaseUrl.replace(/\/+$/, "");
        const response = await fetch(
          `${baseUrl}/auth/push-notifications/online-users`
        );
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.success === false) {
          const debugText = data.debug
            ? ` (${JSON.stringify(data.debug)})`
            : "";
          this.onlineUsersError = `${
            data.msg || data.error || "Unable to load connected users"
          }${debugText}`;
          this.onlineUsers = [];
          return;
        }

        this.onlineUsers = data.users || [];
      } catch (error) {
        this.onlineUsersError = `${error.message}. Check API base URL and make sure /auth/push-notifications is proxied to the API.`;
        this.onlineUsers = [];
      } finally {
        this.isLoadingOnlineUsers = false;
      }
    },
    async send() {
      this.error = "";
      this.response = null;

      if (!this.$refs.form.validate()) {
        return;
      }

      this.isSending = true;
      this.persistSettings();

      try {
        const baseUrl = this.apiBaseUrl.replace(/\/+$/, "");
        const response = await fetch(`${baseUrl}${this.endpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(this.buildPayload()),
        });
        const data = await response.json().catch(() => ({}));
        this.response = {
          httpStatus: response.status,
          ...data,
        };

        if (!response.ok || data.success === false) {
          this.error = data.msg || data.error || "Push notification failed";
        }
      } catch (error) {
        this.error = error.message;
        this.response = {
          success: false,
          error: error.message,
        };
      } finally {
        this.isSending = false;
      }
    },
  },
};
</script>

<style scoped>
.response-output {
  margin: 0;
  min-height: 260px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.socket-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 280px;
}
</style>
